import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import TrueWellnessHeader from "@/components/truewellness/TrueWellnessHeader";
import TrueWellnessFooter from "@/components/truewellness/TrueWellnessFooter";
import PeptideTreatmentCards from "@/components/truewellness/PeptideTreatmentCards";
import MedicalWeightLoss from "@/components/truewellness/MedicalWeightLoss";
import NudaLabsIntro from "@/components/truewellness/NudaLabsIntro";
import FloatingBookCTA from "@/components/truewellness/FloatingBookCTA";
import LearnMoreModal from "@/components/truewellness/LearnMoreModal";
import GoogleRatingBadge from "@/components/ui/GoogleRatingBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  MapPin, 
  Phone, 
  Star,
  Award, 
  Sparkles, 
  Leaf, 
  BadgeCheck, 
  Video,
  ClipboardCheck,
  Pill,
  HeartHandshake,
  MessageCircle,
  RefreshCw,
  Package,
  Users,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import trueLipsIcon from "@/assets/truelips-icon-new.png";

const LocationDetail = () => {
  const { id: slug } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [learnMoreModalOpen, setLearnMoreModalOpen] = useState(false);
  const [bioModalOpen, setBioModalOpen] = useState(false);

  // Fetch location details by slug
  const { data: location, isLoading: locationLoading } = useQuery({
    queryKey: ['location', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      
      if (error) {
        toast.error("Failed to load location");
        throw error;
      }
      
      if (!data) {
        toast.error("Location not found");
        navigate('/locations');
        return null;
      }
      
      return data;
    },
    enabled: !!slug,
  });

  const handleOpenModal = () => {
    setLearnMoreModalOpen(true);
  };

  if (locationLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <TrueWellnessHeader onOpenModal={handleOpenModal} />
        <main className="flex-grow flex items-center justify-center pt-24">
          <p className="text-muted-foreground">Loading location details...</p>
        </main>
        <TrueWellnessFooter />
      </div>
    );
  }

  if (!location) {
    return null;
  }

  const services = [
    { key: 'truewellness', name: 'TrueWellness', icon: Leaf, type: 'lucide', link: null, alwaysShow: true },
    { key: 'truetox', name: 'TrueTox', icon: Sparkles, type: 'lucide', link: 'https://www.mytruetox.com' },
    { key: 'truelips', name: 'TrueLips', icon: trueLipsIcon, type: 'image', link: 'https://www.mytruetox.com' },
    { key: 'trueboost', name: 'TrueBoost', icon: Sparkles, type: 'lucide', link: 'https://www.mytruetox.com' },
    { key: 'truehair', name: 'TrueHair', icon: Sparkles, type: 'lucide', link: null },
  ].filter(service => service.alwaysShow || location[service.key as keyof typeof location]);

  const googleMapsUrl = location.google_maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${location.name} ${location.address}, ${location.city}, ${location.state}`
  )}`;

  const journeySteps = [
    {
      icon: Pill,
      title: "Select Your Stack",
      description: "Browse our curated peptide stacks designed for specific wellness goals—weight loss, recovery, energy, longevity, and more."
    },
    {
      icon: Video,
      title: "Book TeleHealth Consult",
      description: "Meet virtually with your provider to discuss your goals, health history, and confirm your stack selection."
    },
    {
      icon: ClipboardCheck,
      title: "Customize Your Stack",
      description: "Your provider tailors doses, timing, and combinations based on your unique biology and wellness objectives."
    },
    {
      icon: HeartHandshake,
      title: "Optimize Your Stack",
      description: "Regular check-ins and protocol adjustments as your body responds—ensuring you get the best possible results."
    }
  ];

  const supportFeatures = [
    {
      icon: MessageCircle,
      title: "Provider-Led Follow-Ups",
      description: "Regular check-ins with your provider to review progress, adjust dosing, and optimize your protocol."
    },
    {
      icon: RefreshCw,
      title: "Easy Reordering",
      description: "Seamless prescription refills coordinated through our pharmacy partners—no hassle, no delays."
    },
    {
      icon: Package,
      title: "Concierge Logistics",
      description: "Fast fulfillment, transparent pricing, and dedicated support for any questions along the way."
    },
    {
      icon: Users,
      title: "Expert Guidance",
      description: "Access to medical oversight and protocol adjustments as your needs change over time."
    }
  ];

  return (
    <>
      <Helmet>
        <title>{`${location.provider_name || location.name} - ${location.city}, ${location.state} | TrueWellness Provider`}</title>
        <meta 
          name="description" 
          content={`Book a personalized TeleHealth consultation with ${location.provider_name || 'your provider'} in ${location.city}, ${location.state}. Custom peptide protocols tailored to your body's needs.`}
        />
        <link rel="canonical" href={`https://mytruewellness.com/locations/${location.slug}`} />
        
        {/* Local Business Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            "name": location.name,
            "description": location.description || `Premium wellness treatments in ${location.city}, ${location.state}`,
            "address": {
              "@type": "PostalAddress",
              "streetAddress": location.address,
              "addressLocality": location.city,
              "addressRegion": location.state,
              "addressCountry": "US"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": location.latitude,
              "longitude": location.longitude
            },
            "telephone": location.phone,
            "url": `https://mytruewellness.com/locations/${location.slug}`,
            ...(location.google_rating && location.google_review_count ? {
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": location.google_rating.toString(),
                "reviewCount": location.google_review_count.toString(),
                "bestRating": "5",
                "worstRating": "1"
              }
            } : {})
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <TrueWellnessHeader onOpenModal={handleOpenModal} />
        
        <main className="space-y-0">
          {/* Provider Info Card Section - Hero */}
          <section className="pt-28 md:pt-32 pb-12 md:pb-16 bg-gradient-to-b from-[#0D1B2A] to-[#1B263B]">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto">
                {/* Hero Header */}
                <div className="text-center mb-10">
                  <Badge className="mb-4 bg-[#ffdb4d]/10 text-[#ffdb4d] border border-[#ffdb4d]/30 font-semibold">
                    Your Local Provider
                  </Badge>
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                    Your Personalized <span className="text-[#ffdb4d]">Wellness Journey</span>
                  </h1>
                  <p className="text-lg text-white/70 max-w-2xl mx-auto">
                    Every protocol is customized to your body's unique needs through physician-led TeleHealth consultations.
                  </p>
                </div>

                {/* Compact Provider Card */}
                <Card className="bg-white/5 border-white/10 p-6 md:p-8 hover:bg-white/10 hover:border-[#ffdb4d]/30 transition-all duration-300 relative">
                  {/* Clinic Logo - Top Right */}
                  {location.clinic_logo_url && (
                    <div className="absolute top-4 right-4 md:top-6 md:right-6">
                      <img
                        src={location.clinic_logo_url}
                        alt={`${location.name} logo`}
                        className="h-12 md:h-16 w-auto max-w-[120px] md:max-w-[160px] object-contain brightness-0 invert opacity-80"
                      />
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row items-center gap-6">
                    {/* Provider Headshot */}
                    {location.provider_headshot_url && (
                      <div className="relative flex-shrink-0">
                        <img 
                          src={location.provider_headshot_url} 
                          alt={location.provider_name || "Provider"}
                          className="h-28 w-28 md:h-32 md:w-32 object-cover rounded-full border-4 border-[#ffdb4d]/30 shadow-xl"
                        />
                        {location.top_clinic && (
                          <div className="absolute -bottom-1 -right-1 bg-[#ffdb4d] rounded-full p-1.5 shadow-lg">
                            <BadgeCheck className="w-4 h-4 text-[#0D1B2A]" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Provider Info */}
                    <div className="flex-1 text-center sm:text-left space-y-3 pr-0 sm:pr-20">
                      {location.provider_name && (
                        <h3 className="text-2xl md:text-3xl font-bold text-white">
                          {location.provider_name}
                        </h3>
                      )}
                      {location.provider_title && (
                        <p className="text-[#ffdb4d] font-medium">
                          {location.provider_title}
                        </p>
                      )}
                      <p className="text-white/60 text-sm">
                        {location.name}
                      </p>

                      {/* Credentials */}
                      {location.years_experience && (
                        <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                          <div className="flex items-center gap-1.5 bg-white/10 px-3 py-1 rounded-full">
                            <Award className="w-3.5 h-3.5 text-[#ffdb4d]" />
                            <span className="text-white text-xs font-medium">
                              {location.years_experience}+ Years Experience
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Google Rating Badge - Prominent */}
                      {location.google_rating && location.google_review_count && (
                        <div className="mt-2">
                          <GoogleRatingBadge
                            rating={location.google_rating}
                            reviewCount={location.google_review_count}
                            googleMapsUrl={googleMapsUrl}
                            size="sm"
                          />
                        </div>
                      )}

                      {/* View Bio Button */}
                      {location.provider_bio && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-[#ffdb4d]/50 text-[#ffdb4d] hover:bg-[#ffdb4d]/10 font-semibold mt-2"
                          onClick={() => setBioModalOpen(true)}
                        >
                          <Users className="w-4 h-4 mr-2" />
                          View Bio
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Contact Info - Clickable */}
                  <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-4 justify-center">
                    {/* Phone */}
                    {location.phone && (
                      <a 
                        href={`tel:${location.phone.replace(/[^\d]/g, "")}`}
                        className="flex items-center gap-2 text-white hover:text-[#ffdb4d] transition-colors bg-white/5 hover:bg-white/10 px-4 py-3 rounded-xl"
                      >
                        <Phone className="w-5 h-5 text-[#ffdb4d]" />
                        <span className="font-medium">{location.phone}</span>
                      </a>
                    )}
                    
                    {/* Address */}
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-white hover:text-[#ffdb4d] transition-colors bg-white/5 hover:bg-white/10 px-4 py-3 rounded-xl"
                    >
                      <MapPin className="w-5 h-5 text-[#ffdb4d]" />
                      <span className="font-medium text-sm">{location.address}, {location.city}, {location.state}</span>
                    </a>
                  </div>

                  {/* Services */}
                  {services.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2 justify-center">
                      {services.map(service => {
                        const badgeContent = (
                          <>
                            {service.type === 'image' ? (
                              <img src={service.icon as string} alt="" className="w-4 h-4 mr-1.5" />
                            ) : (
                              <service.icon className="w-4 h-4 mr-1.5" />
                            )}
                            {service.name}
                          </>
                        );

                        return service.link ? (
                          <a
                            key={service.key}
                            href={service.link}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Badge 
                              variant="outline"
                              className="border-white/20 text-white/90 bg-white/5 px-3 py-1.5 hover:bg-[#ffdb4d]/20 hover:border-[#ffdb4d]/50 transition-colors cursor-pointer"
                            >
                              {badgeContent}
                            </Badge>
                          </a>
                        ) : (
                          <Badge 
                            key={service.key}
                            variant="outline"
                            className="border-white/20 text-white/90 bg-white/5 px-3 py-1.5"
                          >
                            {badgeContent}
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </section>

          {/* Your Personalized Journey Section */}
          <section className="py-16 md:py-24 bg-[#1B263B]">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                {/* Section Header */}
                <div className="text-center mb-12 md:mb-16">
                  <Badge className="mb-4 bg-[#ffdb4d]/10 text-[#ffdb4d] border border-[#ffdb4d]/30 font-semibold">
                    How It Works
                  </Badge>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                    Four Simple <span className="text-[#ffdb4d]">Steps</span>
                  </h2>
                </div>

                {/* Journey Steps */}
                <div className="grid md:grid-cols-2 gap-6">
                  {journeySteps.map((step, index) => (
                    <Card 
                      key={index}
                      className="bg-white/5 border-white/10 p-6 md:p-8 hover:bg-white/10 hover:border-[#ffdb4d]/30 transition-all duration-300 group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#ffdb4d]/10 flex items-center justify-center group-hover:bg-[#ffdb4d]/20 transition-colors">
                          <step.icon className="w-6 h-6 text-[#ffdb4d]" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-[#ffdb4d]/60 text-sm font-bold">Step {index + 1}</span>
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#ffdb4d] transition-colors">
                            {step.title}
                          </h3>
                          <p className="text-white/70 leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Ongoing Support Section */}
          <section className="py-16 md:py-24 bg-gradient-to-b from-[#1B263B] to-[#0D1B2A]">
            <div className="container mx-auto px-4">
              <div className="max-w-5xl mx-auto">
                {/* Section Header */}
                <div className="text-center mb-12 md:mb-16">
                  <Badge className="mb-4 bg-[#ffdb4d]/10 text-[#ffdb4d] border border-[#ffdb4d]/30 font-semibold">
                    Ongoing Support
                  </Badge>
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
                    You're Never <span className="text-[#ffdb4d]">On Your Own</span>
                  </h2>
                  <p className="text-lg text-white/70 max-w-2xl mx-auto">
                    From your first consultation to your ongoing protocol adjustments, we're with you every step of the way.
                  </p>
                </div>

                {/* Support Features Grid */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                  {supportFeatures.map((feature, index) => (
                    <div 
                      key={index}
                      className="text-center p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-[#ffdb4d]/30 hover:bg-white/10 transition-all duration-300 group"
                    >
                      <div className="w-14 h-14 rounded-xl bg-[#ffdb4d]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#ffdb4d]/20 transition-colors">
                        <feature.icon className="w-7 h-7 text-[#ffdb4d]" />
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-[#ffdb4d] transition-colors">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-white/60 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* =====================================================
              PRODUCT SECTIONS - MIRROR HOME PAGE (TrueWellness.tsx)
              RULE: Location pages should always display the same
              product offerings as the home page for consistency.
              When adding/removing products on home page, update here too.
              ===================================================== */}

          {/* GLP-1 Weight Loss Section */}
          <MedicalWeightLoss onOpenModal={handleOpenModal} orderlyLink={location.orderly_link} />

          {/* Nuda Labs Intro */}
          <NudaLabsIntro onOpenModal={handleOpenModal} nudaLink={location.nuda_link || "https://nudalabs.com/nudalabs-home/"} />

          {/* Peptide Stacks */}
          <PeptideTreatmentCards onOpenModal={handleOpenModal} />

          {/* Final CTA Section */}
          <section className="py-16 md:py-24 bg-gradient-to-b from-[#0D1B2A] to-[#1B263B]">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                  Ready to Get Started?
                </h2>
                <p className="text-lg text-white/70 mb-8">
                  Select your peptide stack and work with {location.provider_name || 'your provider'} to customize it to your body's unique needs.
                </p>
                <Button 
                  size="lg"
                  className="bg-[#ffdb4d] hover:bg-[#ffdb4d]/90 text-[#0D1B2A] font-bold text-lg px-10 py-6"
                  onClick={handleOpenModal}
                >
                  Select Your Stack
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          </section>

          {/* Provider Bio Modal */}
          <Dialog open={bioModalOpen} onOpenChange={setBioModalOpen}>
            <DialogContent className="bg-[#1B263B] border-white/10 text-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
                  {location.provider_headshot_url && (
                    <img 
                      src={location.provider_headshot_url} 
                      alt={location.provider_name || "Provider"}
                      className="h-12 w-12 object-cover rounded-full border-2 border-[#ffdb4d]/30"
                    />
                  )}
                  <div>
                    <span>{location.provider_name}</span>
                    {location.provider_title && (
                      <p className="text-sm text-white/70 font-medium mt-0.5">{location.provider_title}</p>
                    )}
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-4">
                <p className="text-white/80 leading-relaxed">
                  {location.provider_bio}
                </p>
                
                {location.specialties && location.specialties.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-white mb-2">Specialties</h4>
                    <div className="flex flex-wrap gap-2">
                      {location.specialties.map((specialty, idx) => (
                        <Badge key={idx} variant="outline" className="border-white/20 text-white/80 bg-white/5">
                          {specialty}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap gap-3 pt-2">
                  {location.years_experience && (
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                      <Award className="w-4 h-4 text-[#ffdb4d]" />
                      <span className="text-white text-sm font-medium">
                        {location.years_experience}+ Years Experience
                      </span>
                    </div>
                  )}
                  {location.google_rating && (
                    <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                      <Star className="w-4 h-4 text-[#ffdb4d] fill-[#ffdb4d]" />
                      <span className="text-white text-sm font-medium">
                        {location.google_rating} ({location.google_review_count} reviews)
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </main>

        <TrueWellnessFooter />
        <FloatingBookCTA onOpenModal={handleOpenModal} />
        <LearnMoreModal 
          open={learnMoreModalOpen} 
          onOpenChange={setLearnMoreModalOpen}
          redirectToNuda={true}
          nudaLink={location.nuda_link || "https://nudalabs.com/nudavitae-assessment/"}
          sourcePage="location_page"
        />
      </div>
    </>
  );
};

export default LocationDetail;